import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { NewFileUpload } from '@roc/ui';
import { downloadDocument } from '@roc/feature-utils';
import { LeaseUnitService } from '../service/leaseUnitService';
import { LeaseFormStore } from './leaseFormStore';
import { LeaseRow } from '../util/types';

export class LeaseUnitStore {
  globalStore: GlobalStore;
  private leaseUnitService: LeaseUnitService;

  loanId: number | null;
  propertyId: number | null;
  taskId: number | null;
  leases: LeaseRow[];
  processingStatus: string;
  errorMessage: string | null;
  files: NewFileUpload[] = [];
  leaseFormStore: LeaseFormStore;
  leasePreviewData: any = null;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.leaseUnitService = new LeaseUnitService();
    this.leaseFormStore = new LeaseFormStore(globalStore);
    this.loanId = null;
    this.propertyId = null;
    this.taskId = null;
    this.leases = [];
    this.processingStatus = null;
    this.errorMessage = null;

    makeObservable(this, {
      loanId: observable,
      propertyId: observable,
      taskId: observable,
      leases: observable,
      processingStatus: observable,
      errorMessage: observable,
      files: observable,
      leasePreviewData: observable,
      lease: computed,
      getLeaseUnitInfo: flow,
      saveLease: flow,
      uploadFile: flow,
      downloadLease: flow,
      previewLease: flow,
      setFiles: action,
      setContext: action,
      loadEditForm: action,
      reset: action,
      clearPreview: action,
    });
  }

  get lease(): LeaseRow | null {
    return this.leases.find(lease => lease.taskId === this.taskId) ?? null;
  }

  setContext(loanId: number, propertyId: number, taskId: number) {
    this.loanId = loanId;
    this.propertyId = propertyId;
    this.taskId = taskId;
  }

  setFiles(files: NewFileUpload[]) {
    this.files = files;
  }

  clearPreview() {
    this.leasePreviewData = null;
  }

  reset() {
    this.files = [];
    this.leases = [];
    this.processingStatus = null;
    this.errorMessage = null;
    this.leasePreviewData = null;
    this.leaseFormStore.reset();
  }

  loadEditForm(lease: LeaseRow) {
    this.leaseFormStore.reset();
    this.leaseFormStore.loadForm({
      unitNumber: lease.unitNumber ?? '',
      tenantName: lease.tenantName ?? '',
      leaseType: lease.leaseType ?? '',
      startDate: lease.startDate ?? '',
      endDate: lease.endDate ?? '',
      monthlyRent: lease.monthlyRent != null ? String(lease.monthlyRent) : '',
      marketRent: lease.marketRent != null ? String(lease.marketRent) : '',
      vacant: lease.vacant ?? false,
      sectionEightFourTwo: lease.sectionEightFourTwo ? 'true' : 'false',
    });
  }

  *getLeaseUnitInfo() {
    try {
      const response = yield this.leaseUnitService.getLeaseUnitInfo(
        this.loanId,
        this.propertyId,
        this.taskId
      );
      const data = response?.data?.data;
      this.leases = data?.leases ?? [];
      this.processingStatus = data?.processingStatus ?? null;
      this.errorMessage = data?.errorMessage ?? null;
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while fetching lease information',
      });
    }
  }

  *uploadFile() {
    const [upload] = this.files;

    if (!upload) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please upload a document before submitting.',
      });
      return;
    }

    try {
      yield this.leaseUnitService.uploadLeaseUnit(
        upload.file,
        this.loanId,
        this.propertyId,
        this.taskId
      );
      this.files = [];
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Lease document uploaded successfully.',
      });
      yield this.getLeaseUnitInfo();
    } catch {
      this.files = [];
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while uploading the lease document.',
      });
    }
  }

  *saveLease() {
    if (!this.leaseFormStore.runFormValidationWithMessage()) {
      return false;
    }

    const values = this.leaseFormStore.getFormValues();
    const payload: Partial<LeaseRow> = {
      leaseId: this.lease?.leaseId ?? undefined,
      loanId: this.loanId ?? undefined,
      propertyId: this.propertyId ?? undefined,
      taskId: this.taskId ?? undefined,
      unitNumber: values.unitNumber,
      tenantName: values.tenantName,
      leaseType: values.leaseType,
      startDate: values.startDate,
      endDate: values.endDate,
      monthlyRent: values.monthlyRent !== '' ? Number(values.monthlyRent) : null,
      marketRent: values.marketRent !== '' ? Number(values.marketRent) : null,
      vacant: values.vacant === true,
      sectionEightFourTwo: values.sectionEightFourTwo === 'true',
    };

    try {
      yield this.leaseUnitService.saveLeaseUnit(payload);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Lease saved successfully.',
      });
      yield this.getLeaseUnitInfo();
      return true;
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving the lease',
      });
      return false;
    }
  }

  *downloadLease(leaseId: number) {
    try {
      const response = yield this.leaseUnitService.downloadLeaseUnit(leaseId);
      downloadDocument(response?.data, response?.headers, 'download');
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while downloading the lease document.',
      });
    }
  }

  *previewLease(leaseId: number) {
    try {
      this.leasePreviewData = null;
      const response = yield this.leaseUnitService.downloadLeaseUnit(leaseId);
      this.leasePreviewData = response;
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while loading the document preview.',
      });
    }
  }
}
